import {LocalOfferOutlined} from '@mui/icons-material';
import {Box, Button, Typography} from '@mui/material';
import {FC, useMemo} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Link} from 'react-router-dom';
import {LanguageEnum} from 'src/enums';
import {ProceedCurrenciesEnum} from 'src/utils/consts';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import {
    accentBarSx,
    badgeSx,
    cardSx,
    factSx,
    factValueSx,
    factsSx,
    feeAmountSx,
    feeCurrencySx,
    feePeriodSx,
    feeSx,
    headerSx,
    labelSx,
    nameSx,
    openPlanButtonSx,
} from './ContractPlanCard.sx';
import {ContractPlanCardProps} from './ContractPlanCard.types';

/**
 * The plan this contract is on.
 *
 * It used to be TariffCard — the marketing panel from the signing flow — which printed the fee twice
 * and, on this page, linked nowhere at all, though the plan is an entity with a page of its own. Here
 * it is a section that names the plan, prices it once and opens it.
 */
export const ContractPlanCard: FC<ContractPlanCardProps> = ({data, language}) => {
    const intl = useIntl();

    const name = useMemo(() => {
        const byLanguage: Partial<Record<LanguageEnum, string>> = {
            [LanguageEnum.ENGLISH]: data.name,
            [LanguageEnum.FRENCH]: data.nameFrench,
            [LanguageEnum.DEUTSCH]: data.nameDeutsch,
        };

        return byLanguage[language ?? LanguageEnum.ENGLISH] || data.name;
    }, [data, language]);

    const region = useMemo(
        () => [data.country, data.region].filter(Boolean).join(' · ') || '—',
        [data.country, data.region]
    );

    const planLink = AppRouteSpace([AppRouteEnum.app, AppRouteEnum.tariffs, String(data.id)]);

    return (
        <Box sx={cardSx}>
            <Box sx={accentBarSx}/>

            <Box sx={headerSx}>
                <Box sx={badgeSx}>
                    <LocalOfferOutlined/>
                </Box>
                <Box sx={{minWidth: 0}}>
                    <Typography sx={labelSx}>
                        <FormattedMessage id="tariffPlan"/>
                    </Typography>
                    <Typography component={Link} to={planLink} sx={nameSx}>
                        {name}
                    </Typography>
                </Box>

                <Box sx={{flex: '1 1 auto'}}/>

                <Button
                    component={Link}
                    to={planLink}
                    variant="contained"
                    sx={openPlanButtonSx}
                >
                    <FormattedMessage id="openPlan"/>
                </Button>
            </Box>

            <Box sx={factsSx}>
                <Box sx={factSx}>
                    <Typography sx={labelSx}>
                        <FormattedMessage id="monthlyFeeWithoutCurrency"/>
                    </Typography>
                    <Box sx={feeSx}>
                        <Typography component="span" sx={feeCurrencySx}>
                            {ProceedCurrenciesEnum[data.currency]}
                        </Typography>
                        {/* Cents, like every other invoiced amount in the product. */}
                        <Typography component="span" sx={feeAmountSx}>{data.monthlyFee / 100}</Typography>
                        <Typography component="span" sx={feePeriodSx}>
                            {intl.formatMessage({id: 'monthlyFeeDescription'})}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={factSx}>
                    <Typography sx={labelSx}>
                        <FormattedMessage id="minDuration"/>
                    </Typography>
                    <Typography sx={factValueSx}>
                        {intl.formatMessage({id: 'monthsTariff'}, {count: data.contractPeriodLimit})}
                    </Typography>
                </Box>

                <Box sx={factSx}>
                    <Typography sx={labelSx}>
                        <FormattedMessage id="region"/>
                    </Typography>
                    <Typography sx={factValueSx}>{region}</Typography>
                </Box>
            </Box>
        </Box>
    );
};
